import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useInternalNavigate } from '../../../utils/navigate/useNavigate';

import config from '../../../temp/config';
import applyRetrievedQuote from '../../../utils/prospectData/applyRetrievedQuote';
import { RETRIEVAL_OUTCOMES } from '../../../utils/prospectData/validateRetrievedQuote';
import useRecaptcha from '../../../utils/recaptcha/useRecaptcha';
import { pushServiceDownError } from '../../../utils/analytics/datalayer';

const RetrieveQuoteLogic = ({ fields, minDisplayTime }) => {
  const [hasComplete, setHasComplete] = useState(false);
  const [canClose, setCanClose] = useState(false);
  const [redirectTo, setRedirectTo] = useState();
  const navigate = useInternalNavigate();
  const dispatch = useDispatch();
  useRecaptcha();

  const resolveRedirect = (outcome, redirects) => {
    switch (outcome) {
      case RETRIEVAL_OUTCOMES.PRINCIPAL_INVALID:
        return redirects.serviceDownPage;
      case RETRIEVAL_OUTCOMES.DEPENDANT_INVALID:
        return redirects.whoToCoverPage;
      case RETRIEVAL_OUTCOMES.PREVENT_SALE:
        return redirects.existingPlanPage;
      default:
        return redirects.successPage;
    }
  };

  const populateQuoteData = async data => {
    try {
      const result = await applyRetrievedQuote(data, dispatch);

      const redirects = {
        serviceDownPage: fields.serviceDownPage?.value?.href,
        whoToCoverPage: fields.whoToCoverPage?.value?.href,
        existingPlanPage: fields.existingPlanPage?.value?.href,
        successPage: fields.successPage?.value?.href,
      };
      setHasComplete(true);
      const target = resolveRedirect(result.outcome, redirects);
      if (target) {
        setRedirectTo(target);
      }
    } catch (populateError) {
      setHasComplete(true);
      if (fields.serviceDownPage?.value?.href) {
        setRedirectTo(fields.serviceDownPage?.value?.href);
      }
    }
  };

  const retrieveQuote = (emailQuoteRetrievalKey, quoteNo, dateOfBirth, recaptchaToken) => {
    const url = new URL(config?.endpoints?.quotes);
    let params = new URLSearchParams();

    if (emailQuoteRetrievalKey !== null) {
      params.append('emailQuoteRetrievalKey', emailQuoteRetrievalKey);
    } else {
      params.append('quoteNo', quoteNo);
      params.append('dateOfBirth', dateOfBirth);
      params.append('recaptchaToken', recaptchaToken);
    }
    try {
      url.search = params.toString();
      fetch(url.toString(), {
        method: 'GET',
      })
        .then(res => {
          if (!res.ok) {
            pushServiceDownError(url.toString(), res);
            setHasComplete(true);
            if (fields.quoteNotFoundPage?.value?.href) {
              setRedirectTo(fields.quoteNotFoundPage?.value?.href);
            }
            return {};
          }
          return res.json();
        })
        .then(async data => {
          if (JSON.stringify(data) !== '{}') {
            await populateQuoteData(data);
          }
        })
        .catch(err => {
          pushServiceDownError(url.toString(), null, err);
          setHasComplete(true);
          if (fields.serviceDownPage?.value?.href) {
            setRedirectTo(fields.serviceDownPage?.value?.href);
          }
        });
    } catch (error) {
      setHasComplete(true);
      if (fields.serviceDownPage?.value?.href) {
        setRedirectTo(fields.serviceDownPage?.value?.href);
      }
    }
  };

  useEffect(() => {
    if (fields?.retrievalType?.value && typeof window !== 'undefined') {
      let searchParams = new URLSearchParams(window.location.search);
      const emailParam = searchParams.get(fields?.emailRetrieveParam?.value);

      applyTimeout(minDisplayTime, setCanClose);

      if (emailParam === null) {
        if (fields?.quoteNotFoundPage?.value?.href) {
          setRedirectTo(fields?.quoteNotFoundPage?.value?.href);
        }
      } else {
        retrieveQuote(emailParam, null, null, null);
      }
    }
  }, []);

  useEffect(() => {
    if (hasComplete && canClose && redirectTo) {
      navigate(redirectTo);
    }
  }, [hasComplete, canClose, redirectTo]);

  return null;
};

RetrieveQuoteLogic.propTypes = {
  fields: PropTypes.shape({
    successPage: PropTypes.shape({
      value: PropTypes.shape({ href: PropTypes.string }),
    }),
    serviceDownPage: PropTypes.shape({
      value: PropTypes.shape({ href: PropTypes.string }),
    }),
    whoToCoverPage: PropTypes.shape({
      value: PropTypes.shape({ href: PropTypes.string }),
    }),
    quoteNotFoundPage: PropTypes.shape({
      value: PropTypes.shape({ href: PropTypes.string }),
    }),
    retrievalType: PropTypes.shape({ value: PropTypes.string }),
    emailRetrieveParam: PropTypes.shape({ value: PropTypes.string }),
  }),
  minDisplayTime: PropTypes.number,
};

const applyTimeout = function (minDisplayTime, setCanClose) {
  if (minDisplayTime) {
    setTimeout(() => {
      setCanClose(true);
    }, minDisplayTime * 1000);
  } else {
    setCanClose(true);
  }
};

export default RetrieveQuoteLogic;
